import { logger } from '@shared/lib/logger';
import { BOOT_STATUS, type BootStatus, sessionStore } from '@shared/session';

import { BOOT, LEGACY_EXCHANGE_UNTIL } from './api.constant';
import { isApiError } from './api-error';
import { exchangeLegacySession } from './legacy-exchange';
import { legacySessionItem } from './legacy-session';
import { rawClient } from './raw-client';
import { type GetAppStatusResponse, sessionEndpoints } from './session.endpoints';
import { refreshSession } from './token-refresh';

/**
 * 부팅 게이트의 세션 복원.
 * 1. (선택) 서비스 상태 확인 → 점검 중이면 `maintenance`
 * 2. silent refresh(쿠키) → ok: `authed` / rejected: (기한 안이면) 구 웹 로그인 이어받기 → `authed` 또는 `guest`
 *    / unreachable: 백오프 뒤 다시 시도, 끝까지 닿지 못하면 `unreachable`
 *
 * 엔트리(main.tsx)가 **첫 render 전에** `startSessionRestore()` 를 부른다.
 * 공용 HTTP client 는 `whenSessionRestored()` 를 기다린 뒤 요청한다.
 * 이 파일의 요청은 그 게이트를 거치지 않는 `rawClient` 로 보낸다.
 */
export type SettledBootStatus = Exclude<BootStatus, 'booting'>;

export interface SessionRestoreOptions {
  checkAppStatus?: boolean;
  retryBackoffMs?: readonly number[];
  wait?: (ms: number) => Promise<void>;
  /** 지금 시각(구 웹 로그인 이어받기 기한 판단). 테스트에서 바꾼다 */
  now?: () => Date;
}

let restorePromise: Promise<SettledBootStatus> | null = null;

const defaultWait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function isUnderMaintenance(): Promise<boolean> {
  try {
    const { data } = await rawClient.request<GetAppStatusResponse>({
      method: sessionEndpoints.getAppStatus.method,
      url: sessionEndpoints.getAppStatus.path,
    });
    return data.maintenance === true;
  } catch (error) {
    // 상태 확인은 선택 단계다. 점검 코드가 아니면 넘어가고 다음 단계가 판단한다.
    return isApiError(error, 'SERVICE_UNAVAILABLE');
  }
}

/**
 * 구 웹 로그인 이어받기(DD-88). 쿠키 갱신이 거절됐을 때만 부른다.
 * - 기한이 지났거나 구 토큰이 없으면(''도 없음) 이 단계는 아무것도 하지 않는다(부르지도 지우지도 않는다)
 * - ok: 구 토큰을 지운다 / rejected: 지운다 / unreachable: 남겨 두고 다음 부팅에 다시 시도한다
 */
async function continueLegacySession(now: Date): Promise<boolean> {
  if (now.getTime() >= Date.parse(LEGACY_EXCHANGE_UNTIL)) return false;
  const token = legacySessionItem.get();
  if (token === '') return false;

  const outcome = await exchangeLegacySession(token);
  if (outcome !== 'unreachable') legacySessionItem.remove();
  return outcome === 'ok';
}

async function restore(options: SessionRestoreOptions): Promise<SettledBootStatus> {
  const state = sessionStore.getState();
  const checkAppStatus = options.checkAppStatus ?? BOOT.checkAppStatus;
  const backoff = options.retryBackoffMs ?? BOOT.retryBackoffMs;
  const wait = options.wait ?? defaultWait;

  if (checkAppStatus && (await isUnderMaintenance())) {
    state.setMaintenance();
    return BOOT_STATUS.MAINTENANCE;
  }

  for (let attempt = 0; ; attempt += 1) {
    const outcome = await refreshSession();

    if (outcome.result === 'ok') return BOOT_STATUS.AUTHED;

    if (outcome.result === 'rejected') {
      if (await continueLegacySession(options.now?.() ?? new Date())) return BOOT_STATUS.AUTHED;
      state.setGuest();
      return BOOT_STATUS.GUEST;
    }

    if (isApiError(outcome.error, 'SERVICE_UNAVAILABLE')) {
      state.setMaintenance();
      return BOOT_STATUS.MAINTENANCE;
    }

    const delay = backoff[attempt];
    if (delay === undefined) {
      logger.warn('세션을 복원하지 못했습니다(서버에 닿지 못함)', {
        ...(outcome.error.traceId ? { traceId: outcome.error.traceId } : {}),
        code: outcome.error.code,
        attempts: attempt + 1,
      });
      state.setUnreachable();
      return BOOT_STATUS.UNREACHABLE;
    }
    await wait(delay);
  }
}

/** 여러 번 불러도 복원은 한 번만 한다. */
export function startSessionRestore(
  options: SessionRestoreOptions = {},
): Promise<SettledBootStatus> {
  restorePromise ??= restore(options);
  return restorePromise;
}

/** "다시 시도" — 화면을 booting 으로 내리고 처음부터 다시 복원한다. */
export function retrySessionRestore(
  options: SessionRestoreOptions = {},
): Promise<SettledBootStatus> {
  sessionStore.getState().setBooting();
  restorePromise = restore(options);
  return restorePromise;
}

/**
 * 세션 복원 없이 게스트로 시작한다. **요청을 하나도 보내지 않는다.**
 * 서버 없이 시작해야 할 때(화면 테스트의 setup) 쓴다.
 */
export function skipSessionRestore(): void {
  sessionStore.getState().setGuest();
  restorePromise = Promise.resolve(BOOT_STATUS.GUEST);
}

/**
 * HTTP client 가 요청 전에 기다린다.
 * 복원이 시작되지 않았으면 여기서 시작한다 — 복원 전에 요청이 먼저 나가는 일이 없게.
 */
export async function whenSessionRestored(): Promise<void> {
  await startSessionRestore();
}

/** 테스트 전용 */
export function resetSessionRestoreForTest(): void {
  restorePromise = null;
}
